/**
 * Retire an SOP (plan docs/plans/lms-plan.md 11.1 "Retire an SOP: the
 * effective version retired; the course's open assignments cancelled; the
 * record stays"; 8.1 retire; Appendix A POST /admin/sops/:id/retire {reason};
 * WP-46). The reason is required (the course retire rule, 8.1).
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../../../ui/Modal';
import { dangerBtnCls, fieldCls, secondaryBtnCls } from '../../../ui/styles';
import { apiErrorMessage } from '../../api/client';
import { retireSop, SOP_REASON_MAX } from '../../api/sops';
import type { SopRow } from '../../api/sops';
import { ErrorNote, labelCls } from '../../components/pickers/shared';

export interface RetireSopModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRetired: (sop: SopRow) => void;
  sopId: string;
  sopNumber: string;
  sopTitle: string;
  /** The SOP has a training course (its open assignments are cancelled). */
  hasCourse: boolean;
}

export function RetireSopModal({ isOpen, onClose, onRetired, sopId, sopNumber, sopTitle, hasCourse }: RetireSopModalProps) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setReason('');
    setBusy(false);
    setError(null);
  }, [isOpen]);

  const retire = async () => {
    if (busy || !reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const sop = await retireSop(sopId, reason);
      onRetired(sop);
    } catch (err) {
      setError(apiErrorMessage(err, 'The SOP could not be retired.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => {} : onClose} title={`Retire SOP ${sopNumber}`} maxWidth="max-w-lg">
      <div className="space-y-4" data-testid="lms-sop-retire-dialog">
        <p className="text-sm text-gray-700 dark:text-gray-300">{`${sopNumber} ${sopTitle} stops being an SOP of the site.`}</p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
          <li>The effective version is marked retired. Every version stays on record and downloadable by admins.</li>
          {hasCourse && <li>Its training course is retired and every open assignment of it is cancelled.</li>}
          <li>Everyone keeps the acknowledgements and certificates they already hold.</li>
        </ul>
        <div>
          <label htmlFor="lms-sop-retire-reason" className={labelCls}>
            Reason (required)
          </label>
          <textarea
            id="lms-sop-retire-reason"
            data-testid="lms-sop-retire-reason"
            className={`${fieldCls} w-full`}
            rows={3}
            maxLength={SOP_REASON_MAX}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Replaced by SOP 4.3; process discontinued"
          />
        </div>
        {error && <ErrorNote testId="lms-sop-retire-error">{error}</ErrorNote>}
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={dangerBtnCls} onClick={retire} disabled={busy || !reason.trim()} data-testid="lms-sop-retire-confirm">
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Retire SOP
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default RetireSopModal;
